import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { StarRating } from "@/components/StarRating";
import { DisputeBlock } from "@/components/DisputeBlock";
import BusinessCustomerLoyaltyCard from "@/components/BusinessCustomerLoyaltyCard";
import { Lock, Sparkles, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

// Brand palette tuned for rating-coded accents
const ratingTone = (r) => {
  if (r >= 4.5) return { ring: "ring-emerald-300", bg: "bg-emerald-50", text: "text-emerald-900", chip: "bg-emerald-100 text-emerald-800 border-emerald-300", accent: "border-l-emerald-500" };
  if (r >= 3.5) return { ring: "ring-sky-300", bg: "bg-sky-50", text: "text-sky-900", chip: "bg-sky-100 text-sky-800 border-sky-300", accent: "border-l-sky-500" };
  if (r >= 2.5) return { ring: "ring-amber-300", bg: "bg-amber-50", text: "text-amber-900", chip: "bg-amber-100 text-amber-800 border-amber-300", accent: "border-l-amber-500" };
  if (r > 0)   return { ring: "ring-rose-300", bg: "bg-rose-50", text: "text-rose-900", chip: "bg-rose-100 text-rose-800 border-rose-300", accent: "border-l-rose-500" };
  return { ring: "ring-gray-200", bg: "bg-gray-50", text: "text-gray-900", chip: "bg-gray-100 text-gray-800 border-gray-300", accent: "border-l-gray-300" };
};

function initialsFor(name = "") {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[parts.length - 1]?.[0] || "")).toUpperCase() || "?";
}

export default function CustomerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/customers/${id}`);
    setCustomer(data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Please pick a rating");
    setSubmitting(true);
    try {
      await api.post(`/customers/${id}/reviews`, { rating, comment });
      toast.success("Review posted");
      setRating(0);
      setComment("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!customer) return <div className="max-w-5xl mx-auto px-6 py-12 text-gray-500">Loading...</div>;

  const locked = !customer.subscription_active;
  const skeletons = Array.from({ length: customer.locked_count || 3 });
  const tone = ratingTone(customer.rating_avg || 0);
  const ratingText = (customer.rating_avg || 0).toFixed(1);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        {/* HERO */}
        <div className="relative overflow-hidden rounded-2xl border border-border bg-white shadow-sm" data-testid="customer-hero">
          <div className="relative pt-8 pb-8 px-6 md:px-10">
            <div className="flex items-end gap-5 flex-wrap">
              {/* Avatar */}
              <div
                className={`h-24 w-24 rounded-2xl bg-white shadow-md ring-4 ${tone.ring} flex items-center justify-center shrink-0`}
                data-testid="customer-avatar"
              >
                <span className="font-display text-3xl text-[#B31E1E]">{initialsFor(customer.name)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="overline text-gray-500">Customer profile</div>
                <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-1" data-testid="customer-name">
                  {customer.name}
                </h1>
                {customer.address && (
                  <p className="text-gray-600 mt-1.5" data-testid="customer-address">
                    {customer.address}
                  </p>
                )}
              </div>
            </div>

            {/* Rating + stats pills */}
            <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="customer-stats-strip">
              <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${tone.chip}`}>
                <StarRating value={customer.rating_avg} size={16} />
                <span className="text-sm font-semibold tabular-nums">{ratingText}</span>
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-mono text-gray-600">
                <MessageSquareQuote size={12} />
                {customer.rating_count} review{customer.rating_count === 1 ? "" : "s"}
              </div>
              {customer.rating_avg >= 4.5 && customer.rating_count >= 3 && (
                <Badge className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 px-3 py-1.5" data-testid="customer-trusted-badge">
                  <Sparkles size={12} className="mr-1" /> Trusted customer
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* WRITE REVIEW */}
        {!locked && (
          <BusinessCustomerLoyaltyCard customerId={customer.id} customerName={customer.name} />
        )}
        {!locked && <PromoDmCard customerId={customer.id} customerName={customer.name} />}
        {!locked && (
          <Card className="rounded-2xl border border-border mt-8 p-6 shadow-sm bg-white" data-testid="customer-review-form-card">
            <div className="flex items-center gap-2">
              <div className="h-8 w-1 rounded bg-[#B31E1E]" aria-hidden="true" />
              <h3 className="font-display text-2xl">Leave a note on this customer</h3>
            </div>
            <p className="text-sm text-gray-500 mt-1 ml-3">Only other subscribed businesses see this.</p>
            <form className="mt-4 space-y-4" onSubmit={submit}>
              <StarRating value={rating} onChange={setRating} readOnly={false} size={24} testId="customer-review-rating" />
              <Textarea
                rows={4}
                required
                placeholder="Was this customer respectful? On time? Did they pay?"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="rounded-md"
                data-testid="customer-review-comment-input"
              />
              <Button type="submit" disabled={submitting} className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="customer-review-submit-button">
                {submitting ? "Posting..." : "Post review"}
              </Button>
            </form>
          </Card>
        )}

        {/* REVIEWS */}
        <div className="mt-10 relative">
          <h3 className="overline text-gray-500 inline-flex items-center gap-1.5">
            <MessageSquareQuote size={12} /> What businesses say
          </h3>

          {locked ? (
            <div className="mt-4 relative min-h-[280px]">
              <div className="space-y-4 paywall-blur" aria-hidden="true">
                {skeletons.map((_, i) => (
                  <Card key={`pw-skeleton-${i}`} className="rounded-2xl border border-border p-5">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Anonymous business</span>
                      <StarRating value={4} />
                    </div>
                    <p className="mt-2 text-sm text-gray-700">
                      Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus a euismod justo.
                    </p>
                  </Card>
                ))}
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center backdrop-blur-md bg-white/60 border border-white/30 rounded-2xl">
                <Lock className="text-[#B31E1E]" />
                <h4 className="font-display text-2xl mt-4">{customer.locked_count || 0} reviews locked</h4>
                {customer.is_business_viewer ? (
                  <>
                    <p className="text-sm text-gray-600 mt-1 max-w-sm text-center">
                      Subscribe to Servrevu Pro ($9.95/mo) to read what other businesses say about this customer — and add your own.
                    </p>
                    <Link to="/subscription" className="mt-5">
                      <Button className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="paywall-upgrade-button">Unlock with subscription</Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 mt-1 max-w-sm text-center">
                      Individual reviews about customers are only readable by businesses on Servrevu Pro. The public star rating shown above always stays visible.
                    </p>
                    <Link to="/list-your-business" className="mt-5">
                      <Button
                        variant="outline"
                        className="rounded-[35px] border-[#EAE3DE] bg-white hover:bg-[#FAF5F2]"
                        data-testid="paywall-list-business-button"
                      >
                        List your business
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-4" data-testid="customer-reviews">
              {customer.reviews.length === 0 && (
                <p className="text-gray-500 text-sm italic">No reviews yet. Be the first to leave a note.</p>
              )}
              {customer.reviews.map((r, idx) => {
                const t = ratingTone(r.rating);
                return (
                  <Card
                    key={r.id}
                    className={`rounded-2xl border border-border border-l-4 ${t.accent} p-5 bg-white shadow-sm hover:shadow-md transition-shadow fade-up-${(idx % 4) + 1}`}
                    data-testid={`customer-review-${r.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{r.author_name}</span>
                      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${t.chip}`}>
                        <StarRating value={r.rating} size={12} />
                        <span className="text-xs font-semibold tabular-nums">{r.rating}.0</span>
                      </span>
                    </div>
                    <p className="mt-2.5 text-sm text-gray-800 leading-relaxed">{r.comment}</p>
                    <p className="mt-3 text-[11px] font-mono text-gray-400">{new Date(r.created_at).toLocaleDateString()}</p>
                    <DisputeBlock review={r} currentUser={user} onUpdated={load} />
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * PromoDmCard — lets a subscribed business send a promo direct-message to a
 * customer. Backend enforces the 24h cooldown + category match rules; here we
 * only render the form and show the resulting toast.
 */
function PromoDmCard({ customerId, customerName }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    const text = body.trim();
    if (!text) {
      toast.error("Write a short promo message first");
      return;
    }
    if (text.length > 800) {
      toast.error("Please keep promo DMs under 800 characters");
      return;
    }
    setSending(true);
    try {
      await api.post("/business/dm/promo", {
        customer_id: customerId,
        body: text,
      });
      toast.success(`Promo sent to ${customerName}`);
      setBody("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card
      className="rounded-2xl border border-border mt-8 p-6 shadow-sm bg-white"
      data-testid="promo-dm-card"
    >
      <div className="flex items-center gap-2">
        <div className="h-8 w-1 rounded bg-amber-500" aria-hidden="true" />
        <h3 className="font-display text-2xl">Send a promo DM</h3>
      </div>
      <p className="text-sm text-gray-500 mt-1 ml-3 max-w-2xl">
        Deliver a personalised offer straight to this customer's inbox.
        Respects their promo category preferences and the 24-hour cooldown per
        customer. Customers can reply, so keep it friendly and specific.
      </p>
      <div className="mt-4 space-y-3">
        <Textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={`Hi ${customerName.split(" ")[0] || ""}, we saved you a 15% welcome discount…`}
          maxLength={800}
          data-testid="promo-dm-input"
        />
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] font-mono text-gray-400">
            {body.length}/800
          </span>
          <Button
            onClick={send}
            disabled={sending || !body.trim()}
            className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717]"
            data-testid="promo-dm-send-button"
          >
            {sending ? "Sending…" : "Send promo DM"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
